import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FLUID_TYPES,
  SHOCK_BOLUS,
  SURGICAL_RATE_LABEL,
  MAINTENANCE_DAILY,
} from "@/data/fluids";
import { ClipboardList, Calculator, Gauge } from "lucide-react";

/* Clinical fluid order — patient (species + weight), order mode, fluid type and calc helper */
export function OrderCard({
  species,
  weight,
  plan,
  mode,
  fluid,
  order,
  orderText,
  showCalc,
  setSpecies,
  setWeight,
  setMode,
  setFluid,
  setShowCalc,
  resetRun,
}) {
  return (
    <Card className="border-slate-200" data-testid="fluid-order-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#0047AB]" />
          Clinical fluid order
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* patient (species + weight) — drives the rate calculation */}
        <div className="flex items-end gap-3" data-testid="patient-controls">
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-600">Species</label>
            <div className="flex gap-1">
              {[
                { v: "dog", label: "Dog" },
                { v: "cat", label: "Cat" },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => {
                    setSpecies(s.v);
                    resetRun();
                  }}
                  data-testid={`species-${s.v}-btn`}
                  className={`px-3 py-2 rounded-md border-2 text-sm font-semibold transition-all ${
                    species === s.v
                      ? "border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]"
                      : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1 flex-1">
            <label className="text-xs font-medium text-slate-600">Weight (kg)</label>
            <Input
              type="number"
              step="0.1"
              min="0.5"
              value={weight}
              onChange={(e) => {
                setWeight(e.target.value);
                resetRun();
              }}
              data-testid="patient-weight-input"
              className="font-mono-clinical text-base"
            />
          </div>
        </div>

        <div className="flex gap-2">
          {Object.values(plan.orders).map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => {
                setMode(o.id);
                resetRun();
              }}
              data-testid={`order-mode-${o.id}-btn`}
              className={`flex-1 px-3 py-2 rounded-md border-2 text-sm font-semibold transition-all ${
                mode === o.id
                  ? "border-[#0047AB] bg-[#0047AB]/5 text-[#0047AB]"
                  : "border-slate-200 text-slate-500 hover:border-slate-300"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div className="bg-amber-50/60 border border-amber-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-1 gap-2">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700">
              {orderText.vet} · {orderText.heading}
            </div>
            <span
              className="text-[11px] font-mono font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5 whitespace-nowrap"
              data-testid="order-weight-badge"
            >
              {plan.w} kg {plan.sp}
            </span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{orderText.body}</p>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-600">Fluid selected</label>
          <Select value={fluid} onValueChange={setFluid}>
            <SelectTrigger data-testid="fluid-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FLUID_TYPES.map((f) => (
                <SelectItem key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button
          type="button"
          onClick={() => setShowCalc((s) => !s)}
          data-testid="toggle-calc-btn"
          className="flex items-center gap-2 text-xs font-semibold text-[#0047AB] hover:underline"
        >
          <Calculator className="w-3.5 h-3.5" />
          {showCalc ? "Hide" : "Show"} the calculation
        </button>
        {showCalc && (
          <div
            className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 font-mono space-y-1"
            data-testid="calc-helper"
          >
            <div>{orderText.hint}</div>
            <div className="text-slate-900 font-semibold pt-1">
              Target rate = {order.rate} mL/h
            </div>
            <div className="text-slate-900 font-semibold">
              Target VTBI = {order.vtbi} mL ({order.durationHr} hr)
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* AAHA reference rates table for the selected species */
export function ReferenceRatesCard({ plan }) {
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <Gauge className="w-4 h-4 text-[#0047AB]" />
          Reference rates ({plan.sp})
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        <table className="w-full">
          <tbody className="divide-y divide-slate-100">
            <tr>
              <td className="py-2 text-slate-600">Intra-operative (anaesthesia)</td>
              <td className="py-2 text-right font-mono font-semibold text-slate-900">
                {SURGICAL_RATE_LABEL[plan.sp]} mL/kg/hr
              </td>
            </tr>
            <tr>
              <td className="py-2 text-slate-600">Maintenance (conscious)</td>
              <td className="py-2 text-right font-mono font-semibold text-slate-900">
                {plan.perKg.maintenance} mL/kg/hr
                <span className="block text-[10px] font-normal text-slate-400">
                  ≈ {MAINTENANCE_DAILY[plan.sp]} mL/kg/day
                </span>
              </td>
            </tr>
            <tr>
              <td className="py-2 text-slate-600">Shock bolus (titrated)</td>
              <td className="py-2 text-right font-mono font-semibold text-slate-900">
                {SHOCK_BOLUS[plan.sp]} mL/kg
                <span className="block text-[10px] font-normal text-slate-400">
                  over 15–30 min
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <p className="text-[11px] text-slate-400 mt-2">
          2024 AAHA Fluid Therapy Guidelines for Dogs &amp; Cats. Rates are starting
          points, titrated to the patient; reassess if &gt;20 mL/kg is given in a single
          anaesthetic episode.
        </p>
      </CardContent>
    </Card>
  );
}
